import React, { useState, useEffect } from 'react';
import { FiUsers, FiEdit, FiTrash2, FiPlus, FiEye, FiLock, FiMail } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';

const Users = () => {
  const { user: currentUser, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    company: '',
    companyInfo: {
      agence: '',
      bureau: '',
      adresse: '',
      ville: '',
      telephone: '',
      email: ''
    }
  });

  useEffect(() => {
    console.log('Users component mounted');
    console.log('Auth loading:', authLoading);
    console.log('Current user:', currentUser);
    if (currentUser) {
      fetchUsers();
    }
  }, [currentUser]);

  useEffect(() => {
    console.log('Current user changed:', currentUser);
  }, [currentUser]);

  // Show loading while authentication is being checked
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  // Show error if user is not authenticated
  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Accès Refusé</h1>
          <p className="text-gray-600">Vous devez être connecté pour accéder à cette page.</p>
        </div>
      </div>
    );
  }

  // Show error if user doesn't have permission
  if (currentUser.role !== 'superadmin' && currentUser.role !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Permission Insuffisante</h1>
          <p className="text-gray-600">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      console.log('Fetching users...');
      const response = await api.get('/api/auth/users');
      console.log('Users data received:', response.data.data);
      console.log('Users count:', response.data.data.length);
      setUsers(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation before submission
    if (!formData.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('L\'email est requis');
      return;
    }

    if (!editingUser && !formData.password.trim()) {
      toast.error('Le mot de passe est requis pour créer un nouvel utilisateur');
      return;
    }

    if (!editingUser && formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      if (editingUser) {
        // Update existing user
        if (currentUser.role === 'superadmin') {
          // Super admin can update everything (but not password in this update)
          const updateData = { ...formData };
          delete updateData.password; // Don't update password in this flow

          await api.put(`/api/auth/users/${editingUser._id}`, updateData);
        } else {
          // Admin can only update basic info
          await api.put(`/api/auth/users/${editingUser._id}/basic`, {
            name: formData.name
          });
        }
        toast.success('Utilisateur mis à jour avec succès!');
      } else {
        // Create new user (only super admin)
        if (currentUser.role !== 'superadmin') {
          toast.error('Seul l\'Admin Super peut créer des utilisateurs');
          return;
        }

        // Prepare data for creation
        const createData = {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          role: formData.role,
          company: formData.company?.trim() || '',
          companyInfo: formData.companyInfo
        };

        console.log('Creating user with data:', createData);
        await api.post('/api/auth/users', createData);
        toast.success('Utilisateur créé avec succès!');
      }

      setShowModal(false);
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'staff',
        company: '',
        companyInfo: {
          agence: '',
          bureau: '',
          adresse: '',
          ville: '',
          telephone: '',
          email: ''
        }
      });
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);

      // More specific error handling
      if (error.response?.status === 400) {
        if (error.response.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response.data?.error) {
          toast.error(error.response.data.error);
        } else {
          toast.error('Données invalides. Vérifiez les informations saisies.');
        }
      } else if (error.response?.status === 409) {
        toast.error('Un utilisateur avec cet email existe déjà.');
      } else if (error.response?.status === 403) {
        toast.error('Permission refusée. Vérifiez vos droits d\'accès.');
      } else if (error.response?.status === 401) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde de l\'utilisateur');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      company: user.company || '',
      phone: user.phone || '',
      companyInfo: {
        name: user.companyInfo?.name || user.company || '',
        agence: user.companyInfo?.agence || '',
        bureau: user.companyInfo?.bureau || '',
        adresse: user.companyInfo?.adresse || user.companyInfo?.address || '',
        address: user.companyInfo?.address || '',
        ville: user.companyInfo?.ville || user.companyInfo?.city || '',
        city: user.companyInfo?.city || '',
        telephone: user.companyInfo?.telephone || user.phone || '',
        email: user.companyInfo?.email || user.email || '',
        website: user.companyInfo?.website || '',
        description: user.companyInfo?.description || ''
      }
    });
    setShowModal(true);
  };

  const handlePasswordChange = async (userId) => {
    const newPassword = prompt('Entrez le nouveau mot de passe (minimum 6 caractères):');

    if (!newPassword) return;

    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await api.put(`/api/auth/users/${userId}/password`, { password: newPassword });
      toast.success('Mot de passe mis à jour avec succès!');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Erreur lors de la mise à jour du mot de passe');
    }
  };

  const handleDelete = async (userId) => {
    console.log('Fonction de suppression appelée avec userId:', userId);
    console.log('Rôle de l\'utilisateur actuel:', currentUser?.role);

    if (currentUser.role !== 'superadmin') {
      toast.error('Seul l\'Admin Super peut supprimer des utilisateurs');
      return;
    }

    if (window.confirm('Voulez-vous vraiment supprimer cet utilisateur?')) {
      try {
        console.log('Tentative de suppression de l\'utilisateur:', userId);
        const response = await api.delete(`/api/auth/users/${userId}`);
        console.log('Réponse de suppression:', response);
        toast.success('Utilisateur supprimé avec succès!');
        fetchUsers();
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'utilisateur:', error);
        console.error('Réponse d\'erreur:', error.response);
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur');
      }
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'staff',
      company: '',
      companyInfo: {
        agence: '',
        bureau: '',
        adresse: '',
        ville: '',
        telephone: '',
        email: ''
      }
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'staff',
      company: '',
      companyInfo: {
        agence: '',
        bureau: '',
        adresse: '',
        ville: '',
        telephone: '',
        email: ''
      }
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'bg-red-600 text-white';
      case 'admin':
        return 'bg-brand-green text-white';
      case 'staff':
        return 'bg-green-600 text-white';
      case 'viewer':
        return 'bg-gray-600 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const canEditUser = (user) => {
    if (currentUser.role === 'superadmin') return true;
    if (currentUser.role === 'admin' && user.role !== 'superadmin') return true;
    return false;
  };

  const canDeleteUser = (user) => {
    const canDelete = currentUser.role === 'superadmin' && user._id !== currentUser._id;
    console.log('canDeleteUser check:', {
      userRole: user.role,
      userId: user._id,
      currentUserRole: currentUser?.role,
      currentUserId: currentUser?._id,
      canDelete
    });
    return canDelete;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Debug current user state
  console.log('Current user state:', {
    currentUser,
    loading,
    usersCount: users.length
  });

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <FiUsers className="mr-3" />
          Gestion des Agences
        </h1>
        {currentUser?.role === 'superadmin' && (
          <button
            onClick={openCreateModal}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center"
          >
            <FiPlus className="mr-2" />
            Créer une Agence
          </button>
        )}
      </div>

      {/* Role Information */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-primary-800 mb-2">Gestion des Agences:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-primary-700">
          <div>
            <strong>Super Admin:</strong> Contrôle total - peut créer, modifier, supprimer des agences et gérer tous les utilisateurs
          </div>
          <div>
            <strong>Admin (Agence Rental):</strong> Peut voir et mettre à jour les informations de base des utilisateurs (noms uniquement), ne peut pas changer les mots de passe ou emails
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilisateur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière Connexion
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => {
                console.log('Rendering user:', {
                  id: user._id,
                  name: user.name,
                  role: user.role,
                  canDelete: canDeleteUser(user),
                  canEdit: canEditUser(user)
                });
                return (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.company || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {user.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Jamais'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {canEditUser(user) && (
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-primary-600 hover:text-primary-900"
                            title="Modifier l'Utilisateur"
                          >
                            <FiEdit className="h-4 w-4" />
                          </button>
                        )}
                        {currentUser?.role === 'superadmin' && (
                          <button
                            onClick={() => handlePasswordChange(user._id)}
                            className="text-green-600 hover:text-green-900"
                            title="Changer le mot de passe"
                          >
                            <FiLock className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteUser(user) ? (
                          <button
                            onClick={() => {
                              console.log('Delete button clicked for user:', user);
                              console.log('User ID:', user._id);
                              console.log('User ID type:', typeof user._id);
                              handleDelete(user._id);
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Supprimer l'Utilisateur"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">
                            {currentUser?.role === 'superadmin' ? 'Impossible de se supprimer soi-même' : 'Pas de permission'}
                          </span>
                        )}
                        {currentUser?.role === 'admin' && (
                          <span className="text-gray-400" title="L'admin ne peut modifier que les noms">
                            <FiLock className="h-4 w-4" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {
        showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingUser ? 'Modifier l\'Agence' : 'Créer une Nouvelle Agence'}
                </h3>
                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {(!editingUser || currentUser?.role === 'superadmin') && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={!editingUser}
                        disabled={editingUser && currentUser?.role === 'admin'}
                      />
                      {editingUser && currentUser?.role === 'admin' && (
                        <p className="text-xs text-gray-500 mt-1">
                          <FiLock className="inline mr-1" />
                          Seul le Super Admin peut changer les emails
                        </p>
                      )}
                    </div>
                  )}

                  {!editingUser && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mot de passe
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  )}

                  {currentUser?.role === 'superadmin' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rôle
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="staff">Personnel</option>
                        <option value="admin">Admin (Agence Rental)</option>
                        <option value="viewer">Observateur</option>
                      </select>
                    </div>
                  )}

                  {/* Company Information - Only for Admin (Agence Rental) users */}
                  {currentUser?.role === 'superadmin' && formData.role === 'admin' && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom de l'Entreprise
                        </label>
                        <input
                          type="text"
                          value={formData.company || ''}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Nom de l'entreprise"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Agence
                          </label>
                          <input
                            type="text"
                            value={formData.companyInfo?.agence || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              companyInfo: { ...formData.companyInfo, agence: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nom de l'agence"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Bureau
                          </label>
                          <input
                            type="text"
                            value={formData.companyInfo?.bureau || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              companyInfo: { ...formData.companyInfo, bureau: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nom du bureau"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Adresse
                          </label>
                          <input
                            type="text"
                            value={formData.companyInfo?.adresse || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              companyInfo: { ...formData.companyInfo, adresse: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Adresse complète"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ville
                          </label>
                          <input
                            type="text"
                            value={formData.companyInfo?.ville || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              companyInfo: { ...formData.companyInfo, ville: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Nom de la ville"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Téléphone
                          </label>
                          <input
                            type="text"
                            value={formData.companyInfo?.telephone || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              companyInfo: { ...formData.companyInfo, telephone: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Numéro de téléphone"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email de contact
                          </label>
                          <input
                            type="email"
                            value={formData.companyInfo?.email || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              companyInfo: { ...formData.companyInfo, email: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Email de contact"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      {editingUser ? 'Mettre à Jour' : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Users;
